import { Search } from "@mui/icons-material";
import {
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useGlossary } from "../../../hooks/useGlossary";
import {
  GlossaryEntry,
  GlossaryGuide,
  isMetric,
  isSearchOnly,
} from "../../../models/Glossary";
import ErrorCard from "../../Elements/ErrorCard";
import GlossaryEntryRow from "./GlossaryEntryRow";
import GlossaryGuideRow from "./GlossaryGuideRow";

type Kind = "all" | "filters" | "metrics";

const CHIP_SX = (active: boolean) => ({
  height: 26,
  borderRadius: "3px",
  fontSize: "0.75rem",
  cursor: "pointer",
  bgcolor: active ? "#4c4c4c" : "transparent",
  color: active ? "#fff" : "inherit",
  border: "1px solid #bdbdbd",
  "&:hover": { bgcolor: active ? "#4c4c4c" : "grey.100" },
});

/**
 * Ranks an entry against a search term. Name matches beat body matches, because
 * someone typing "TS" wants TS% ahead of every metric whose formula mentions TSA.
 */
function score(entry: GlossaryEntry, term: string): number {
  const name = entry.name.toLowerCase();
  if (name === term) return 0;
  if (name.startsWith(term)) return 1;
  if (name.includes(term)) return 2;
  const body = [
    entry.description,
    entry.group,
    isMetric(entry) ? entry.formula : entry.expression,
    isMetric(entry) ? entry.counts.join(" ") : entry.column,
    isMetric(entry) ? "" : (entry.values ?? []).join(" "),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return body.includes(term) ? 3 : -1;
}

const guideMatches = (g: GlossaryGuide, term: string) =>
  [g.title, g.summary, g.body, g.metrics.join(" ")].join(" ").toLowerCase().includes(term);

const SECTION_HEADER_SX = {
  position: "sticky",
  top: 0,
  zIndex: 2,
  px: 1.25,
  py: 0.5,
  bgcolor: "#4c4c4c",
  color: "#fff",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
} as const;

const SECTION_LABEL_SX = {
  fontSize: "0.68rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
} as const;

interface Props {
  /** Rendered inside the query builder dialog rather than as a full page. */
  compact?: boolean;
  /** A guide to open and scroll to on load (the page's `?guide=` deep link). */
  initialGuide?: string | null;
}

export default function GlossaryContent({ compact = false, initialGuide = null }: Props) {
  const { glossary, loading, error } = useGlossary();
  const [search, setSearch] = useState("");
  const [kind, setKind] = useState<Kind>("all");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [openGuide, setOpenGuide] = useState<string | null>(initialGuide);
  // Set when a guide is opened from elsewhere (deep link, an entry's link); cleared once scrolled to.
  const scrollTo = useRef<string | null>(initialGuide);
  const guideRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const guides = useMemo(() => {
    const all = glossary?.guides ?? [];
    const term = search.trim().toLowerCase();
    if (kind === "filters") return [];
    return term ? all.filter((g) => guideMatches(g, term)) : all;
  }, [glossary, search, kind]);
  const guideTitles = useMemo(
    () => Object.fromEntries((glossary?.guides ?? []).map((g) => [g.id, g.title])),
    [glossary],
  );

  useEffect(() => {
    const id = scrollTo.current;
    if (!id || !guides.some((g) => g.id === id)) return;
    guideRefs.current[id]?.scrollIntoView({ block: "start" });
    scrollTo.current = null;
  }, [guides, openGuide]);

  const showGuide = (id: string) => {
    setSearch("");
    setKind("all");
    setOpenGuide(id);
    scrollTo.current = id;
  };

  const showMetric = (name: string) => {
    setKind("all");
    setSearch(name);
    setExpanded(`metric:${name}`);
  };

  const { groups, hiddenMatches } = useMemo(() => {
    if (!glossary) return { groups: [], hiddenMatches: 0 };
    const term = search.trim().toLowerCase();
    let entries: GlossaryEntry[] = [
      ...(kind === "metrics" ? [] : glossary.filters),
      ...(kind === "filters" ? [] : glossary.metrics),
    ];

    if (term) {
      // Searching reaches everything, including entries kept out of the browse
      // list — that is the only way to find one, so it must be exhaustive.
      const matched = entries
        .map((e) => ({ e, s: score(e, term) }))
        .filter(({ s }) => s >= 0)
        .sort((a, b) => a.s - b.s || a.e.name.localeCompare(b.e.name))
        .map(({ e }) => e);
      return {
        groups: [{ group: "", entries: matched }],
        hiddenMatches: matched.filter(isSearchOnly).length,
      };
    }

    entries = entries.filter((e) => !isSearchOnly(e));
    const byGroup = new Map<string, GlossaryEntry[]>();
    entries.forEach((e) => {
      const key = `${e.kind === "filter" ? "Filters" : "Metrics"} · ${e.group}`;
      byGroup.set(key, [...(byGroup.get(key) ?? []), e]);
    });
    return {
      groups: [...byGroup.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([group, list]) => ({ group, entries: list })),
      hiddenMatches: 0,
    };
  }, [glossary, search, kind]);

  const total = groups.reduce((n, g) => n + g.entries.length, 0);

  if (error && !glossary) return <ErrorCard message={error} />;

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: compact ? 1.5 : 2,
          pt: compact ? 1 : 1.5,
          pb: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        {!compact && (
          <Stack
            direction="row"
            alignItems="baseline"
            spacing={1.5}
            flexWrap="wrap"
            sx={{ mb: 1 }}
          >
            <Typography
              sx={{
                fontSize: "1rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.3px",
              }}
            >
              Glossary
            </Typography>
            {glossary && (
              <Typography sx={{ fontSize: "0.72rem", color: "rgba(0,0,0,0.55)" }}>
                {glossary.coverage.filters} filters · {glossary.coverage.metrics}{" "}
                metrics · definitions from {glossary.sources.vendor.length}{" "}
                provider{glossary.sources.vendor.length === 1 ? "" : "s"}
              </Typography>
            )}
          </Stack>
        )}

        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
          <TextField
            size="small"
            autoFocus={compact}
            placeholder="Search names, definitions, values, formulas…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 180 }}
          />
          {(["all", "filters", "metrics"] as Kind[]).map((k) => (
            <Chip
              key={k}
              label={k}
              size="small"
              onClick={() => setKind(k)}
              sx={{ ...CHIP_SX(kind === k), textTransform: "capitalize" }}
            />
          ))}
        </Stack>

        {!search && (
          <Typography sx={{ fontSize: "0.68rem", color: "rgba(0,0,0,0.45)", mt: 0.75 }}>
            Rarely used and retired entries are left out of this list — search to
            find them.
          </Typography>
        )}
        {!!hiddenMatches && (
          <Typography sx={{ fontSize: "0.68rem", color: "rgba(0,0,0,0.45)", mt: 0.75 }}>
            {hiddenMatches} of these {hiddenMatches === 1 ? "is" : "are"} rarely
            used or retired — see the tag beside the name.
          </Typography>
        )}
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0, bgcolor: "#fafafa" }}>
        {loading && !glossary ? (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress size={24} />
          </Stack>
        ) : total === 0 && guides.length === 0 ? (
          <Typography sx={{ p: 3, fontSize: "0.85rem", color: "rgba(0,0,0,0.55)" }}>
            {search ? `Nothing matches “${search}”.` : "Nothing to show."}
          </Typography>
        ) : (
          <>
          {guides.length > 0 && (
            <Box>
              <Box sx={SECTION_HEADER_SX}>
                <Typography sx={SECTION_LABEL_SX}>Guides</Typography>
                <Typography sx={{ fontSize: "0.68rem", opacity: 0.7 }}>{guides.length}</Typography>
              </Box>
              {guides.map((g) => (
                <GlossaryGuideRow
                  key={g.id}
                  ref={(el) => {
                    guideRefs.current[g.id] = el;
                  }}
                  guide={g}
                  expanded={openGuide === g.id}
                  onToggle={() => setOpenGuide((cur) => (cur === g.id ? null : g.id))}
                  onShowMetric={showMetric}
                />
              ))}
            </Box>
          )}
          {groups.map(({ group, entries }) => (
            <Box key={group || "results"}>
              {group && (
                <Box sx={SECTION_HEADER_SX}>
                  <Typography sx={SECTION_LABEL_SX}>{group}</Typography>
                  <Typography sx={{ fontSize: "0.68rem", opacity: 0.7 }}>
                    {entries.length}
                  </Typography>
                </Box>
              )}
              <Box sx={{ bgcolor: "#fff" }}>
                {entries.map((entry) => {
                  const key = `${entry.kind}:${entry.name}`;
                  return (
                    <GlossaryEntryRow
                      key={key}
                      entry={entry}
                      expanded={expanded === key}
                      onToggle={() =>
                        setExpanded((cur) => (cur === key ? null : key))
                      }
                      guideTitle={entry.guide ? guideTitles[entry.guide] : undefined}
                      onOpenGuide={showGuide}
                    />
                  );
                })}
              </Box>
            </Box>
          ))}
          </>
        )}
      </Box>
    </Box>
  );
}
